/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { motion, AnimatePresence } from 'framer-motion'
import { Droppable } from 'react-beautiful-dnd'
import { useMedia } from 'react-use'

import { PlanGroupTitle } from '../planGroupTitle'
import { CommentCard, CommentCount } from '../comment'
import { PlanItemCard, PlanItemCourseInput, PlanItemPlaceholderInput } from '../../item'
import { PrimaryButton, SecondaryButton } from '../../../shared/buttons'
import { Menu } from '../../../shared/menu'
import {
  CreatePlanItemInput,
  BoardGroupFragment,
  BoardItemFragment,
  TermOption,
  BoardAlertFragment,
  PlanningTerm,
  AlertLevel
} from '../../../../generated/graphql'
import { oneDpShadowCss } from '../../../../lib/styles/shadows'
import { offsetGray2 } from '../../../../lib/styles/colors'
import { BP_SMALL_DOWN } from '../../../../lib/styles/breakpoints'
import useAppSettings from '../../../../lib/hooks/useAppSettings'
import useModal from '../../../../lib/hooks/useModal'
import { ModalName } from '../../../../types'
import { userCanLoginToScheduler, userIsOnlyStudent } from '../../../../lib/authenticationUtils'
import { srOnlyCss } from '../../../../lib/styles/cssStyles'
import { useReadOnly } from '../../../../lib/hooks/useReadOnly'
import { useAuth } from '../../../provider/OIDCProvider'

export type PlanGroupLaneProps = {
  /** List of alerts belonging to the group */
  alerts: Array<BoardAlertFragment>
  /** Campus used to filter courses */
  campus?: string
  /** Current Plan Group */
  group: BoardGroupFragment
  /** Indicates whether the group is linked to an active term */
  isActiveTerm: boolean
  /** Indicates if this is the user's primary plan */
  isPrimaryPlan?: boolean | undefined
  /** If the item being added is a course only (no adding placeholder items) */
  isCourseOnly?: boolean
  /** Indicates if this plan is a template */
  isTemplate: boolean
  /** How many new comments there are for the group */
  newCommentCount: number
  /** Event callback when Add Comment is clicked from the menu */
  onAddComment: () => void
  /** Event callback when a placeholder is selected from the search options */
  onAddPlaceholderItem: (placeholder: BoardItemFragment) => void
  /** Event callback when a new Catalog Course is selected or placeholder is added */
  onAddPlanItem: (planItem: CreatePlanItemInput) => void
  /** Event callback when adding a comment is cancelled */
  onCancelAddComment: () => void
  /** Event callback when the cancel add button is clicked */
  onCancelAddItem: () => void
  /** Even callback when the comment count is clicked */
  onCountClick: () => void
  /** Event callback when Delete Term is clicked */
  onDeleteGroup: () => void
  /** Event callback when marking comments as read */
  onMarkCommentsRead: () => void
  /** Event callback when the save comment button is clicked */
  onSaveComment?: () => void
  /** Event callback when Schedule is clicked */
  onScheduleClick?: () => void
  /** Event callback when add course is clicked */
  onShowCourseInput?: () => void
  /** Event callback when Add Item is clicked */
  onShowInput: () => void
  /** Event callback when Add Placeholder is clicked */
  onShowPlaceholderInput?: () => void
  /** The ownerAppUserId of the plan owner */
  ownerAppUserId: number
  /** Whether the plan/template is read-only */
  readOnly?: boolean
  /** Show the add comment field when clicking Add Comment from the group menu */
  showAddComment: boolean
  /** Show the comments when clicking on the comment count button */
  showComments: boolean
  /** Show Item Input/Autocomplete */
  showInput: boolean
  /** Hide the new comment indicator after the comments have been viewed */
  showNewCommentIndicator: boolean
  /** Show Placeholder input/Autocomplete */
  showPlaceholderInput?: boolean
  /** Optional list of configured term options to use for template term naming */
  termOptions?: Array<TermOption>
  /** List of terms to display in the term select on a plan */
  terms?: Array<PlanningTerm>
  /** Total number of comments for the group */
  totalCommentCount: number
}

const defaultProps: Pick<
  PlanGroupLaneProps,
  | 'campus'
  | 'isPrimaryPlan'
  | 'isCourseOnly'
  | 'onSaveComment'
  | 'onScheduleClick'
  | 'onShowCourseInput'
  | 'onShowPlaceholderInput'
  | 'readOnly'
  | 'showPlaceholderInput'
  | 'termOptions'
  | 'terms'
> = {
  campus: undefined,
  isCourseOnly: false,
  isPrimaryPlan: false,
  onSaveComment: undefined,
  onScheduleClick: undefined,
  onShowCourseInput: undefined,
  onShowPlaceholderInput: undefined,
  readOnly: false,
  showPlaceholderInput: false,
  termOptions: undefined,
  terms: undefined
}

export const groupLaneCss = css`
  background-color: ${offsetGray2};
  border-radius: 4px;
  padding-bottom: 12px;
`
const itemsContainerCss = css`
  padding-left: 8px;
  padding-right: 8px;
`
// necessary evil for dragging item into empty list
const itemListCss = css`
  min-height: 0.1px;
  padding-top: 8px;
`
const addItemCardCss = css`
  align-items: center;
  display: grid;
  height: 58px;
`

const buttonCss = css`
  height: 100%;
  width: 100%;
`

const scheduleButtonContainerCss = css`
  a {
    text-decoration: none;
  }
  display: flex;
  justify-content: center;
  padding-bottom: 8px;
  width: 100%;
`

const PlanGroupLane = ({
  alerts,
  campus,
  group,
  isActiveTerm,
  isCourseOnly,
  isPrimaryPlan,
  isTemplate,
  newCommentCount,
  onAddComment,
  onAddPlanItem,
  onAddPlaceholderItem,
  onCancelAddComment,
  onCancelAddItem,
  onCountClick,
  onDeleteGroup,
  onMarkCommentsRead,
  onSaveComment,
  onScheduleClick,
  onShowCourseInput,
  onShowInput,
  onShowPlaceholderInput,
  ownerAppUserId,
  readOnly,
  showAddComment,
  showComments,
  showInput,
  showPlaceholderInput,
  showNewCommentIndicator,
  termOptions,
  terms,
  totalCommentCount
}: PlanGroupLaneProps) => {
  const { items, id, externalTermId, planId } = group
  const { showModal } = useModal()
  const isSmall = useMedia(`(${BP_SMALL_DOWN})`)
  const { text } = useAppSettings()
  const { course, coursePlural, schedule: scheduleLabel, term: termLabel } = text
  const { user } = useAuth() || {}
  const canLoginToScheduler = userCanLoginToScheduler(user, ownerAppUserId)
  const isStudent = userIsOnlyStudent(user)
  const { isReadOnly } = useReadOnly()

  // items is an immutable immer array, so slice before sorting
  const sortedItems =
    !!items &&
    items
      .slice()
      .sort((a: BoardItemFragment, b: BoardItemFragment) =>
        a.displaySequence > b.displaySequence ? 1 : -1
      )

  const onAdd = (item: CreatePlanItemInput) => {
    onAddPlanItem(item)
  }

  const handleAddComment = () => {
    if (isSmall) {
      showModal(ModalName.AddComment, {
        groupId: group.id,
        id: 'comment',
        isTemplate,
        readOnly: (readOnly && !isReadOnly) || false, // if it isReadOnly with a provider we can add comments
        showAddComment: true,
        showComments: false,
        showNewCommentIndicator
      })
      onMarkCommentsRead()
    } else {
      onAddComment()
    }
  }

  const handleCountClick = () => {
    if (isSmall) {
      showModal(ModalName.AddComment, {
        groupId: group.id,
        id: 'comment',
        isTemplate,
        readOnly: readOnly || false,
        showAddComment: false,
        showComments: true,
        showNewCommentIndicator
      })
      onMarkCommentsRead()
    } else {
      onCountClick()
    }
  }

  const warningAlertCount = alerts.filter(
    // filter out audit alerts, there might be a cleaner way to do this
    (a) => a.level !== AlertLevel.Success
  ).length

  const menuItems = [
    {
      text: course,
      disabled: false,
      onClick: onShowCourseInput
    },
    {
      text: 'Placeholder',
      disabled: false,
      onClick: onShowPlaceholderInput
    }
  ]

  return (
    <motion.div>
      <motion.div
        animate={{ opacity: 1, scale: 1 }}
        css={[groupLaneCss, oneDpShadowCss]}
        exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
        initial={{ opacity: 0, scale: 0.3 }}
        layout
      >
        <PlanGroupTitle
          alertCount={warningAlertCount}
          group={group}
          isTemplate={isTemplate}
          onAddComment={handleAddComment}
          onDelete={onDeleteGroup}
          readOnly={readOnly}
          showAddComment={showAddComment}
          termOptions={termOptions}
          terms={terms}
        />
        <div css={itemsContainerCss}>
          <Droppable droppableId={id!} type="item">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} css={itemListCss}>
                {sortedItems &&
                  sortedItems.map((item: BoardItemFragment) => (
                    <PlanItemCard
                      key={item.id!}
                      alerts={alerts}
                      campus={campus}
                      group={group}
                      isTemplate={isTemplate}
                      item={item}
                      readOnly={readOnly}
                    />
                  ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          {!showInput && !showPlaceholderInput && !readOnly && (
            <div css={addItemCardCss}>
              {isStudent && (
                <SecondaryButton center css={buttonCss} onClick={onShowInput}>
                  <span aria-hidden="true">+</span> Add
                  <span css={srOnlyCss}>{` ${course} or placeholder`}</span>
                </SecondaryButton>
              )}
              {!isStudent && (
                <Menu
                  ariaLabel={`Add a ${course} or placeholder`}
                  buttonCss={buttonCss}
                  displayTooltip={false}
                  id={`${group.id}-add-item`}
                  label="+ Add"
                  menuItems={menuItems}
                />
              )}
            </div>
          )}
          <AnimatePresence initial={false}>
            {showInput && (
              <motion.div
                key="input"
                initial={{ opacity: 0, scale: 0.3 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, y: -100, transition: { duration: 0.1 } }}
              >
                <PlanItemCourseInput
                  campus={campus}
                  groupId={id!}
                  isCourseOnly={isCourseOnly}
                  isTemplate={isTemplate}
                  onAdd={onAdd}
                  onCancelAdd={onCancelAddItem}
                  planId={planId}
                  termCode={externalTermId || ''}
                />
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence initial={false}>
            {showPlaceholderInput && (
              <motion.div
                key="input"
                initial={{ opacity: 0, scale: 0.3 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, y: -100, transition: { duration: 0.1 } }}
              >
                <PlanItemPlaceholderInput
                  campus={campus}
                  groupId={id!}
                  onAdd={onAddPlaceholderItem}
                  onCancelAdd={onCancelAddItem}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {isActiveTerm && isPrimaryPlan && canLoginToScheduler && (
          <div css={scheduleButtonContainerCss}>
            <PrimaryButton
              ariaLabel={`${scheduleLabel} ${coursePlural} for ${termLabel} ${group.title}`}
              onClick={onScheduleClick}
            >
              {scheduleLabel}
            </PrimaryButton>
          </div>
        )}
      </motion.div>
      <CommentCount
        isTemplate={isTemplate}
        onCountClick={handleCountClick}
        newCommentCount={newCommentCount}
        showNewCommentIndicator={showNewCommentIndicator}
        totalCommentCount={totalCommentCount}
      />
      <AnimatePresence initial={false}>
        {(showAddComment || showComments) && !isSmall && (
          <motion.div
            key="comment"
            initial={{ opacity: 0, scale: 0.3 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, y: -100, transition: { duration: 0.1 } }}
          >
            <CommentCard
              comments={group.comments}
              group={group}
              isTemplate={isTemplate}
              onCancel={onCancelAddComment}
              onSaveComment={onSaveComment}
              readOnly={readOnly}
              showAddComment={showAddComment}
              showComments={showComments}
              showNewCommentIndicator={showNewCommentIndicator}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

PlanGroupLane.defaultProps = defaultProps

export default PlanGroupLane
